import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * Shared Button primitive — first entry in the `components/ui/`
 * design-system layer (shadcn/ui conventions on the Radix + CVA +
 * tailwind-merge stack that was already installed; see
 * `docs/design/design-system.md`).
 *
 * Variants are derived from the app's EXISTING visual language, not
 * imported from shadcn's defaults:
 *
 *   - `default`   — accent-filled action (crucible-accent tokens).
 *   - `secondary` — the `border-white/[0.12] bg-white/[0.06]` chip
 *                   pattern used across the new-UI workspace
 *                   (e.g. the phone-cap "Change" control).
 *   - `ghost`     — borderless hover-wash row/menu actions.
 *   - `glass`     — the floating blurred pill used by the overlay
 *                   action clusters over the preview (Back / Reload /
 *                   Restart in `PreviewOverlay`).
 *
 * `app-region-no-drag` is baked into the base class: every button in
 * the Electron shell must opt out of drag-region hit-testing or
 * clicks get eaten by the window-drag region (issues #531 / #804).
 * Harmless in non-Electron consumers (crucible-web).
 */
const buttonVariants = cva(
  // Standard button shape is FULLY ROUNDED: text buttons read as pills,
  // and the `icon` size becomes a circle. `rounded-full` lives in the base
  // class so it's the system default, not a per-call-site override.
  'app-region-no-drag inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-[transform,color,background-color,border-color,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent/60 active:scale-[0.97] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40',
  {
    variants: {
      variant: {
        // `bg-crucible-accent` is the solid fallback (legacy + light themes);
        // `.brand-gradient-fill` layers the Canary → Clementine gradient on
        // top in dark new-UI. Text colour comes from the brand-contrast rule
        // in globals.css (dark ink on the bright fill).
        default: 'brand-gradient-fill bg-crucible-accent text-white hover:bg-crucible-accent-hover',
        secondary:
          'border border-white/[0.12] bg-white/[0.06] text-white/72 hover:border-white/[0.24] hover:text-white',
        ghost: 'text-white/72 hover:bg-white/[0.08] hover:text-white',
        glass:
          'border border-white/[0.08] bg-[rgba(20,16,26,0.72)] text-[#f1ecf7] shadow-[0_6px_18px_rgba(0,0,0,0.35)] backdrop-blur-[8px]',
      },
      size: {
        default: 'px-4 py-2 text-[13px]',
        sm: 'px-3 py-1 text-[11px]',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render the child element with button classes/behaviour merged in
   *  (Radix `Slot`), e.g. styling an `<a>` like a button. */
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        // Default to type="button" — bare <button> defaults to
        // "submit" and fires form submission when nested in a form.
        // Slot targets may not accept `type`, so only set it on the
        // real element.
        {...(asChild ? {} : { type: type ?? 'button' })}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
